import { useState, useRef, useEffect, createContext, useContext, useMemo, useCallback } from 'react'
import { useChatDraft } from '@/hooks/useChatDraft'
import { ArrowUp, ArrowRight, Square, MessageCircle, Check, X, Loader2, ChevronDown, ChevronRight, Rocket, Plus, ArrowDown, RotateCcw, PartyPopper, CreditCard, ExternalLink, Play, Pause, Volume2, Snowflake, LifeBuoy } from 'lucide-react'
import { useAppModals } from '@/contexts/app-modals-context'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useAction, useMutation, useQuery } from 'convex/react'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import * as Sentry from '@sentry/react'
import { getStripe, lightAppearance } from '@/lib/stripe'
import { api } from '../../../convex/_generated/api'
import type { Id } from '../../../convex/_generated/dataModel'
import { useAuth } from '@/contexts/auth-context'
import { useFF } from '@/platform/feature-flags/useFeatureFlag'
import { useVideoSeekOptional } from '@/contexts/video-seek-context'
import { Button } from '@/components/ui/button'
import { PLANS } from '@/lib/config/plans'
import { cn } from '@/lib/utils'
import { track } from '@/lib/track'
import { formatToolInline, formatToolInputFallbackDetail, type ToolDisplayContext } from '@/lib/config/toolDisplay'
import { StreamingText } from './StreamingText'
import { AssetLibrarySheet } from './AssetLibrarySheet'
import { Lightbox } from '@/components/ui/lightbox'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { VisualToolWidget, widgetWantsWideLane } from './VisualToolWidgets'
import { toast } from '@/hooks/use-toast'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { getPresetVoicePreviewUrl } from '@/lib/voicePresets'
import { CDN_URL } from '@/lib/config/site'
import {
  computeAggregatedToolCalls,
  computeLatestTreatmentToolId,
  computeMessageTerminalStatus,
  findExistingAnswer,
  parseAgentErrorContent,
  type ToolCall,
  type ChatAttachment,
  type ImageAttachment,
  type AudioAttachment,
  type DocumentAttachment,
  type ChatMessage,
} from '@/lib/chatMessages'

// Context for scene lookup in tool displays
const ToolDisplayContextProvider = createContext<ToolDisplayContext>({})

function useToolDisplayContext() {
  return useContext(ToolDisplayContextProvider)
}

export type {
  ToolCall,
  ImageAttachment,
  VideoAttachment,
  AudioAttachment,
  DocumentAttachment,
  ChatAttachment,
  ChatMessage,
} from '@/lib/chatMessages'

export interface PendingImage {
  file: File
  preview: string
}

export interface PendingAudio {
  file: File
  duration: number
}

export interface PendingDocument {
  file: File
}

export interface WorkflowClip {
  _id: string
  title?: string
  orderIndex?: number
}

interface WorkflowChatProps {
  workflowId: Id<'workflows'> | null
  messages: ChatMessage[]
  isStreaming: boolean
  error: string | null
  onSendMessage: (
    message: string,
    opts?: { images?: File[]; audioFiles?: File[]; documents?: File[]; attachments?: ChatAttachment[] }
  ) => void
  onCancel: () => void
  isUploading?: boolean
  uploadStatus?: string | null
  clips?: WorkflowClip[]
  hideComposer?: boolean
  /**
   * When true, the message rows are constrained to a centered narrow column so
   * the scroll container can span the full panel width while content aligns
   * with a narrower composer. Tailwind class is applied to direct children of
   * the scroll container.
   */
  narrowContent?: boolean
  /**
   * Override for the scroll-to-bottom button position. Useful when the parent
   * floats its own composer/pills above the chat and the default offset doesn't
   * clear them.
   */
  scrollButtonClassName?: string
}

function friendlyErrorMessage(error: string): string | null {
  if (/file format is invalid or unsupported/i.test(error)) {
    return 'This file type isn\'t supported. Please upload an image (JPEG, PNG, GIF, or WebP).'
  }
  if (/image\.source\.base64/i.test(error) || /Could not process image/i.test(error)) {
    return 'We couldn\'t process this image. Please try a different file.'
  }
  if (/rate limit/i.test(error) || /429/i.test(error)) {
    return 'Too many requests. Please wait a moment and try again.'
  }
  if (/overloaded/i.test(error) || /529/i.test(error)) {
    return 'The AI service is temporarily busy. Please try again in a moment.'
  }
  return null
}

function ToolCallInlineDisplay({ tool, showActive = false }: { tool: ToolCall; showActive?: boolean }) {
  const ctx = useToolDisplayContext()
  const { name, activeName, detail, icon } = formatToolInline(tool.name, tool.input, ctx)
  const displayName = showActive && tool.status === 'running' ? activeName : name

  const displayDetail = detail || formatToolInputFallbackDetail(tool.input, ctx)

  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-muted-foreground">{icon}</span>
      <span>{displayName}</span>
      {displayDetail && (
        <span className="text-muted-foreground font-normal">· {displayDetail}</span>
      )}
    </span>
  )
}

export function ToolCallsSection({ tools }: { tools: ToolCall[] }) {
  const [expanded, setExpanded] = useState(false)

  const runningTools = tools.filter(t => t.status === 'running')
  const completedTools = tools.filter(t => t.status !== 'running')
  const hasRunning = runningTools.length > 0

  // Sort: running first, then completed/failed reversed (most recent first)
  const sortedTools = [...runningTools, ...completedTools.reverse()]

  // Single tool - simple inline display
  if (tools.length === 1) {
    const tool = tools[0]
    return (
      <div className={cn(
        "inline-flex items-center gap-1 text-[12px] py-0.5 px-1.5 rounded-md",
        tool.status === 'running' ? "text-primary bg-primary/8" : "text-muted-foreground"
      )}>
        {tool.status === 'running' ? (
          <Loader2 size={12} className="text-primary animate-spin shrink-0" />
        ) : tool.status === 'completed' ? (
          <Check size={12} className="text-success/70 shrink-0" />
        ) : (
          <X size={12} className="text-destructive shrink-0" />
        )}
        <ToolCallInlineDisplay tool={tool} showActive />
      </div>
    )
  }

  // Multiple tools - collapsed summary showing running first
  const primaryTool = sortedTools[0]

  return (
    <div className="text-[13px] text-muted-foreground">
      <button
        onClick={() => setExpanded(!expanded)}
        className={cn(
          "flex items-center gap-1 py-0.5 px-1.5 rounded-md text-left transition-colors",
          hasRunning ? "text-primary bg-primary/8 hover:bg-primary/12" : "hover:text-secondary-foreground"
        )}
      >
        {hasRunning ? (
          <Loader2 size={12} className="text-primary animate-spin shrink-0" />
        ) : (
          <Check size={12} className="text-success/70 shrink-0" />
        )}
        <ToolCallInlineDisplay tool={primaryTool} showActive />
        <span className={cn("text-[10px]", hasRunning ? "text-primary/50" : "text-muted-foreground/60")}>
          {hasRunning
            ? runningTools.length > 1
              ? `+${runningTools.length - 1} running, ${completedTools.length} done`
              : `${completedTools.length} done`
            : `+${tools.length - 1}`}
        </span>
        {expanded ? (
          <ChevronDown size={10} className={cn("shrink-0", hasRunning ? "text-primary/50" : "text-muted-foreground/50")} />
        ) : (
          <ChevronRight size={10} className={cn("shrink-0", hasRunning ? "text-primary/50" : "text-muted-foreground/50")} />
        )}
      </button>
      {expanded && (
        <div className="ml-3 border-l border-border/60 pl-1.5 space-y-0">
          {sortedTools.slice(1).map((tool) => (
            <div key={tool.id} className="flex items-center gap-1.5 py-0.5">
              {tool.status === 'running' ? (
                <Loader2 size={10} className="text-primary animate-spin shrink-0" />
              ) : tool.status === 'completed' ? (
                <Check size={10} className="text-success/70 shrink-0" />
              ) : (
                <X size={10} className="text-destructive shrink-0" />
              )}
              <ToolCallInlineDisplay tool={tool} showActive />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// Inline community chat suggestion card
function CommunityChatCard() {
  const isMember = useQuery(api.community.isMember, {})

  if (isMember) {
    return (
      <button
        onClick={() => window.dispatchEvent(new Event('open-community-chat'))}
        className="w-full rounded-2xl border border-border bg-secondary p-4 text-left hover:bg-muted transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-foreground text-background">
            <MessageCircle className="h-4 w-4" />
          </div>
          <div>
            <div className="text-[15px] font-medium text-foreground">Open Founder Chat</div>
            <div className="text-[13px] text-muted-foreground">Ask the community</div>
          </div>
        </div>
      </button>
    )
  }

  return (
    <button
      onClick={() => window.dispatchEvent(new Event('open-community-chat'))}
      className="w-full rounded-2xl border border-border bg-secondary p-4 text-left hover:bg-muted transition-colors"
    >
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-foreground text-background">
          <MessageCircle className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <div className="text-[15px] font-medium text-foreground">Join the Founder Chat</div>
          <div className="text-[13px] text-muted-foreground">Connect with founders and other users for help</div>
        </div>
      </div>
    </button>
  )
}

// Inline support chat suggestion card — opens the live 1:1 support panel when
// the agent calls suggest_support_chat.
function SupportChatCard() {
  const { openSupport } = useAppModals()
  return (
    <button
      onClick={openSupport}
      className="w-full rounded-2xl border border-border bg-secondary p-4 text-left hover:bg-muted transition-colors"
    >
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-foreground text-background">
          <LifeBuoy className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <div className="text-[15px] font-medium text-foreground">Talk to support</div>
          <div className="text-[13px] text-muted-foreground">Chat live with the team about this</div>
        </div>
      </div>
    </button>
  )
}

// Upgrade card shown when show_upgrade_card tool fires. The card itself only
// has a CTA — clicking it opens a single shared modal at the chat root that
// mounts Stripe's PaymentElement. The webhook flips planStatus, which auto-
// routes any visible card to the "You're upgraded!" branch via useAuth().
const upgradeCardStripePromise = getStripe()

const paymentElementFields = {
  billingDetails: { address: 'auto' as const },
}

const paymentElementTerms = {
  applePay: 'never' as const,
  auBecsDebit: 'never' as const,
  bancontact: 'never' as const,
  card: 'never' as const,
  cashapp: 'never' as const,
  googlePay: 'never' as const,
  ideal: 'never' as const,
  klarna: 'never' as const,
  paypal: 'never' as const,
  sepaDebit: 'never' as const,
  sofort: 'never' as const,
  usBankAccount: 'never' as const,
}

type UpgradeModalCtx = { open: (trialEligible: boolean) => void }
const UpgradeModalContext = createContext<UpgradeModalCtx | null>(null)

function useUpgradeModal() {
  return useContext(UpgradeModalContext)
}

function UpgradeModalForm({ trialEligible, clientSecret }: { trialEligible: boolean; clientSecret: string }) {
  const stripe = useStripe()
  const elements = useElements()
  const markCheckoutPending = useMutation(api.stripe.markCheckoutPending)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submitLabel = trialEligible ? 'Start 3-day Trial for $1' : 'Upgrade to Pro'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements) return
    setSubmitting(true)
    setError(null)
    track('upgrade_card.clicked', { plan: 'pro', source: trialEligible ? 'trial' : 'direct' })
    try {
      const { error: submitError } = await elements.submit()
      if (submitError) {
        // PaymentElement renders card/validation errors inline next to the field; don't duplicate.
        if (submitError.type !== 'card_error' && submitError.type !== 'validation_error') {
          setError(submitError.message ?? 'Please check your card details')
        }
        Sentry.captureException(new Error(submitError.message ?? 'Stripe elements.submit failed'), {
          tags: { source: 'upgrade_modal', step: 'elements_submit', stripe_type: submitError.type },
          extra: { code: submitError.code },
        })
        setSubmitting(false)
        return
      }
      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      })
      if (confirmError) {
        if (confirmError.type !== 'card_error' && confirmError.type !== 'validation_error') {
          setError(confirmError.message ?? 'Payment failed')
        }
        Sentry.captureException(new Error(confirmError.message ?? 'Stripe confirmPayment failed'), {
          tags: { source: 'upgrade_modal', step: 'confirmPayment', stripe_type: confirmError.type },
          extra: { code: confirmError.code, decline_code: confirmError.decline_code },
        })
        setSubmitting(false)
        return
      }
      // Success — keep spinner; the modal will close when planStatus flips via webhook.
      // Stamp the pending upgrade so the agent knows payment is processing during
      // the webhook lag (ISS-536). Fire-and-forget: failure just means status quo.
      markCheckoutPending({}).catch(() => {})
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong'
      setError(message)
      Sentry.captureException(err, { tags: { source: 'upgrade_modal', step: 'submit_exception' } })
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement options={{ layout: 'tabs', fields: paymentElementFields, terms: paymentElementTerms }} />
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 text-[12px] text-red-900 px-3 py-2">{error}</div>
      )}
      <div className="pt-1 space-y-2">
        <Button
          type="submit"
          disabled={submitting || !stripe || !elements}
          size="default"
          className="w-full font-semibold text-[16px] h-12 rounded-full bg-[#0385ff] text-white hover:bg-[#0270d8] shadow-[0_2px_8px_rgba(3,133,255,0.25)]"
        >
          {submitting ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing…</>
          ) : (
            <>
              {submitLabel}
              <ArrowRight className="h-4 w-4 ml-2" />
            </>
          )}
        </Button>
        <p className="text-center text-[13px] text-muted-foreground">
          {trialEligible ? (
            <>$1 trial fee ensures quality service. <span className="font-semibold text-foreground">Cancel anytime.</span></>
          ) : (
            <>All plans are no commitment. <span className="font-semibold text-foreground">Cancel anytime.</span></>
          )}
        </p>
      </div>
    </form>
  )
}

function UpgradeModal({
  open,
  onOpenChange,
  trialEligible,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  trialEligible: boolean
}) {
  const createIntent = useAction(api.stripe.createInlineCheckoutIntent)
  const { user } = useAuth()
  const isPaid = user?.planStatus === 'active' || user?.planStatus === 'trialing'
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [intentError, setIntentError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setClientSecret(null)
      setIntentError(null)
      return
    }
    let cancelled = false
    createIntent({ planId: 'pro', trial: trialEligible || undefined })
      .then(({ clientSecret }) => {
        if (!cancelled) setClientSecret(clientSecret)
      })
      .catch((err) => {
        if (cancelled) return
        const message = err instanceof Error ? err.message : 'Could not start checkout'
        setIntentError(message)
        Sentry.captureException(err, { tags: { source: 'upgrade_modal', step: 'createIntent' } })
      })
    return () => {
      cancelled = true
    }
  }, [open, trialEligible, createIntent])

  // Auto-close once the webhook flips planStatus to active/trialing.
  useEffect(() => {
    if (open && isPaid) onOpenChange(false)
  }, [open, isPaid, onOpenChange])

  const [visible, setVisible] = useState(false)
  useEffect(() => {
    if (open) requestAnimationFrame(() => setVisible(true))
    else setVisible(false)
  }, [open])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div
        className={`absolute inset-0 bg-neutral-200/60 backdrop-blur-[1px] transition-opacity duration-150 ${visible ? 'opacity-100' : 'opacity-0'}`}
      />
      <div
        className={`relative z-10 w-full max-w-[440px] rounded-2xl bg-card border border-neutral-300 shadow-[0_12px_30px_-12px_rgba(0,0,0,0.14)] px-6 py-6 transition-all duration-150 ${
          visible ? 'opacity-100 scale-100' : 'opacity-0 scale-[0.97]'
        }`}
      >
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="absolute top-4 right-4 z-20 p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <h2 className="text-center text-[20px] font-bold text-foreground mb-5">Upgrade now</h2>

        {intentError && (
          <div className="rounded-md border border-red-200 bg-red-50 text-[12px] text-red-900 px-3 py-2">{intentError}</div>
        )}

        {!clientSecret && !intentError && (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {clientSecret && (
          <Elements
            stripe={upgradeCardStripePromise}
            options={{ clientSecret, appearance: lightAppearance }}
          >
            <UpgradeModalForm trialEligible={trialEligible} clientSecret={clientSecret} />
          </Elements>
        )}
      </div>
    </div>
  )
}

function UpgradeModalProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false)
  const [trialEligible, setTrialEligible] = useState(false)

  const ctx = useMemo<UpgradeModalCtx>(() => ({
    open: (trial) => {
      setTrialEligible(trial)
      setOpen(true)
    },
  }), [])

  return (
    <UpgradeModalContext.Provider value={ctx}>
      {children}
      <UpgradeModal
        open={open}
        onOpenChange={setOpen}
        trialEligible={trialEligible}
      />
    </UpgradeModalContext.Provider>
  )
}

function getUpgradePitchCopy(variant: string, trialEligible: boolean, price: number) {
  if (variant === 'artifact-anchor') {
    return {
      headline: 'Keep building this video',
      body: trialEligible
        ? <>Unlock generation for <span className="text-foreground font-semibold">$1</span> — a 3-day Pro trial with enough generation for a handful of clips, then ${price}/mo. Cancel anytime.</>
        : <>Unlock full generation for <span className="text-foreground font-semibold">${price}/mo</span>. Cancel anytime.</>,
      cta: trialEligible ? 'Keep Building — $1' : 'Keep Building',
    }
  }
  if (variant === 'outcome-pitch') {
    return {
      headline: 'Export your video in HD',
      body: trialEligible
        ? <>Get your render in HD for <span className="text-foreground font-semibold">$1</span> — a 3-day Pro trial with enough generation for a handful of clips, then ${price}/mo. Cancel anytime.</>
        : <>Get the full render for <span className="text-foreground font-semibold">${price}/mo</span>. Cancel anytime.</>,
      cta: trialEligible ? 'Unlock HD Export — $1' : 'Unlock HD Export',
    }
  }
  return {
    headline: trialEligible ? 'Trial Offer' : 'Upgrade Offer',
    body: trialEligible
      ? <>Try Pro for <span className="text-foreground font-semibold">$1</span> — a 3-day trial with enough generation for a handful of clips, then ${price}/mo. Cancel anytime.</>
      : <>Upgrade to Pro for <span className="text-foreground font-semibold">${price}/mo</span> — full access. Cancel anytime.</>,
    cta: trialEligible ? 'Start $1 Trial' : 'Upgrade to Pro',
  }
}

function UpgradeCard() {
  const { user } = useAuth()
  const modal = useUpgradeModal()
  const pitchFlag = useFF('exp_upgrade_card_pitch_v1')
  const isPaid = user?.planStatus === 'active' || user?.planStatus === 'trialing'
  const trialEligible = !user?.planStatus && !user?.stripeSubscriptionId
  const proPlan = PLANS.pro
  const variant = typeof pitchFlag === 'string' ? pitchFlag : 'control'
  const copy = getUpgradePitchCopy(variant, trialEligible, proPlan.price)

  if (isPaid) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
        <div className="flex items-center gap-2">
          <PartyPopper className="h-5 w-5 text-emerald-600" />
          <span className="text-[15px] font-semibold text-emerald-900">You're upgraded! You may now continue.</span>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <Rocket className="h-4 w-4 text-teal-600" />
        <span className="text-[15px] font-semibold text-foreground">{copy.headline}</span>
      </div>
      <p className="text-[14px] text-muted-foreground mb-3 leading-snug">{copy.body}</p>
      <Button
        type="button"
        onClick={() => modal?.open(!!trialEligible)}
        size="default"
        className="w-full font-semibold text-[15px] h-10 rounded-lg bg-[#0385ff] text-white hover:bg-[#0270d8]"
      >
        {copy.cta}
      </Button>
    </div>
  )
}

// Inline card for managing subscription (fix payment or upgrade plan)
function ManageSubscriptionCard({ portalUrl, reason }: { portalUrl: string; reason: string }) {
  const isPaymentIssue = reason === 'fix_payment'

  return (
    <div className={cn(
      "rounded-2xl p-4 text-white",
      isPaymentIssue
        ? "bg-linear-to-br from-amber-500 to-orange-600"
        : "bg-linear-to-br from-violet-500 to-purple-600"
    )}>
      <div className="flex items-center gap-2 mb-2">
        <CreditCard className="h-4 w-4 opacity-80" />
        <span className="text-[15px] font-semibold">
          {isPaymentIssue ? 'Update Payment' : 'Manage Subscription'}
        </span>
      </div>
      <p className="text-[15px] opacity-90 mb-4">
        {isPaymentIssue
          ? 'Your payment needs attention. Update your billing info to continue generating.'
          : 'Manage your plan, upgrade, or update billing details.'}
      </p>
      <Button
        onClick={() => window.open(portalUrl, '_blank')}
        size="sm"
        className="w-full bg-white text-foreground hover:bg-white/90 font-semibold text-[15px] h-9 rounded-lg shadow-sm"
      >
        <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
        {isPaymentIssue ? 'Fix Payment' : 'Manage Plan'}
      </Button>
    </div>
  )
}

function formatElapsed(seconds: number) {
  if (seconds < 60) return `${seconds}s`
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}m ${secs}s`
}

// Live-updating elapsed seconds since startTime. Ticks every second.
function useElapsedSeconds(startTime: number) {
  const [elapsed, setElapsed] = useState(0)
  useEffect(() => {
    const update = () => setElapsed(Math.max(0, Math.floor((Date.now() - startTime) / 1000)))
    update()
    const id = setInterval(update, 1000)
    return () => clearInterval(id)
  }, [startTime])
  return elapsed
}

function getRenderProgress(elapsedSeconds: number, expectedSeconds = 180) {
  const ratio = elapsedSeconds / expectedSeconds
  if (ratio <= 1) {
    return 0.95 * (1 - (1 - ratio) ** 2)
  }
  const overtime = (elapsedSeconds - expectedSeconds) / 60
  return 0.95 + 0.05 * (overtime / (overtime + 1))
}

function RenderingPill({ startTime, sceneName, clipNum }: { startTime: number; sceneName: string; clipNum?: string }) {
  const elapsed = useElapsedSeconds(startTime)
  const progress = getRenderProgress(elapsed)

  return (
    <div className="flex justify-center py-0.5">
      <span className="relative z-0 text-[11px] font-medium px-2.5 py-0.5 rounded-full border border-blue-200/60 flex items-center gap-1 overflow-hidden">
        <span
          className="absolute inset-0 bg-blue-50 rounded-full origin-left transition-[width] duration-1000 ease-out"
          style={{ width: `${(progress * 100).toFixed(1)}%` }}
        />
        <Loader2 size={10} className="animate-spin text-blue-500 relative z-10" />
        <span className="text-blue-600 relative z-10">{clipNum ? `Clip ${clipNum}` : `"${sceneName}"`}</span>
        <span className="text-blue-300 relative z-10">·</span>
        <span className="text-blue-500 relative z-10">Rendering</span>
        <span className="text-blue-300 relative z-10">·</span>
        <span className="tabular-nums text-blue-500 relative z-10">{formatElapsed(elapsed)}</span>
      </span>
    </div>
  )
}

function ExportingPill({ startTime }: { startTime: number }) {
  const elapsed = useElapsedSeconds(startTime)
  const progress = getRenderProgress(elapsed, 120)

  return (
    <div className="flex justify-center py-0.5">
      <span className="relative z-0 text-[11px] font-medium px-2.5 py-0.5 rounded-full border border-emerald-200/60 flex items-center gap-1 overflow-hidden">
        <span
          className="absolute inset-0 bg-emerald-50 rounded-full origin-left transition-[width] duration-1000 ease-out"
          style={{ width: `${(progress * 100).toFixed(1)}%` }}
        />
        <Loader2 size={10} className="animate-spin text-emerald-500 relative z-10" />
        <span className="text-emerald-600 relative z-10">Exporting</span>
        <span className="text-emerald-300 relative z-10">·</span>
        <span className="tabular-nums text-emerald-500 relative z-10">{formatElapsed(elapsed)}</span>
      </span>
    </div>
  )
}

function SystemMessage({ message, messageTerminalStatus }: { message: ChatMessage; messageTerminalStatus: Map<string, 'ready' | 'cancelled' | 'failed'> }) {
  const lowerContent = message.content.toLowerCase()
  const isError = lowerContent.includes('failed')
  const isComplete = lowerContent.includes('complete') || lowerContent.includes('ready')
  const isGenerating = lowerContent.includes('begun') || lowerContent.includes('generating') || lowerContent.includes('started')

  // Check if this is a video generation message - extract clip number and scene info
  const videoGenMatch = message.content.match(/Clip (\d+) "([^"]+)" video generation begun/) || message.content.match(/Clip "([^"]+)" video generation begun/)
  const clipNum = videoGenMatch?.[2] ? videoGenMatch[1] : undefined
  const sceneName = videoGenMatch?.[2] || videoGenMatch?.[1]
  // Look up terminal status by this message's ID
  const terminalStatus = messageTerminalStatus.get(message.id)
  const isVideoRendering = !!videoGenMatch && !isError && !isComplete && !terminalStatus

  // Parse out the bracketed prefix for cleaner display
  const cleanContent = message.content.replace(/^\[[\w_]+\]\s*/, '')

  // Transcription complete — show minimal pill without raw transcript text
  const transcriptionMatch = message.content.match(/\[TRANSCRIPTION_COMPLETE\].*?Clip (?:(\d+)\s+)?"([^"]+)"/)
  if (transcriptionMatch) {
    const [, tClipNum, tClipName] = transcriptionMatch
    return (
      <div className="flex justify-center -my-2">
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted/60 text-muted-foreground">
          {tClipNum ? `Clip ${tClipNum}` : `"${tClipName}"`} · Transcribed
        </span>
      </div>
    )
  }

  // For video rendering that's complete (ready), show muted small pill
  if (videoGenMatch && terminalStatus === 'ready') {
    return (
      <div className="flex justify-center -my-2">
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted/60 text-muted-foreground">
          {clipNum ? `Clip ${clipNum}` : `"${sceneName}"`} · Done
        </span>
      </div>
    )
  }

  // For video rendering that was cancelled
  if (videoGenMatch && terminalStatus === 'cancelled') {
    return (
      <div className="flex justify-center -my-2">
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted/60 text-muted-foreground">
          {clipNum ? `Clip ${clipNum}` : `"${sceneName}"`} · Cancelled
        </span>
      </div>
    )
  }

  // For video rendering that failed
  if (videoGenMatch && terminalStatus === 'failed') {
    return (
      <div className="flex justify-center -my-2">
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-destructive/8 text-destructive">
          {clipNum ? `Clip ${clipNum}` : `"${sceneName}"`} · Failed
        </span>
      </div>
    )
  }

  // For video rendering in progress, show timer with progress fill
  if (isVideoRendering && message.timestamp && sceneName) {
    return <RenderingPill startTime={message.timestamp} sceneName={sceneName} clipNum={clipNum} />
  }

  // Export messages
  const isExportStarted = message.content.startsWith('[EXPORT_STARTED]')
  const isExportComplete = message.content.startsWith('[EXPORT_COMPLETE]')
  const isExportFailed = message.content.startsWith('[EXPORT_FAILED]')

  if (isExportStarted) {
    const exportTerminal = messageTerminalStatus.get(message.id)
    if (exportTerminal === 'ready') {
      return (
        <div className="flex justify-center -my-2">
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-success/8 text-success">
            Export · Done
          </span>
        </div>
      )
    }
    if (exportTerminal === 'failed') {
      return (
        <div className="flex justify-center -my-2">
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-destructive/8 text-destructive">
            Export · Failed
          </span>
        </div>
      )
    }
    if (message.timestamp) {
      return <ExportingPill startTime={message.timestamp} />
    }
  }

  // Hide export complete/failed messages (they're shown as terminal states on the started pill)
  if (isExportComplete || isExportFailed) {
    return null
  }

  if (isGenerating) {
    return (
      <div className="flex justify-center py-0.5">
        <span className="text-[11px] font-medium px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200/60 flex items-center gap-1 animate-pulse">
          <Loader2 size={10} className="animate-spin shrink-0" />
          {cleanContent}
        </span>
      </div>
    )
  }

  return (
    <div className="flex justify-center -my-1.5">
      <span className={`text-[11px] px-2 py-0.5 rounded-full ${
        isError
          ? 'bg-destructive/10 text-destructive'
          : isComplete
            ? 'bg-success/10 text-success'
            : 'bg-muted text-muted-foreground'
      }`}>
        {cleanContent}
      </span>
    </div>
  )
}

// Minimum gap in minutes to show a timestamp separator
const TIMESTAMP_GAP_MINUTES = 5

export function TimestampSeparator({ timestamp }: { timestamp: number }) {
  const formatRelativeTime = (ts: number) => {
    const now = Date.now()
    const diffMs = now - ts
    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffMinutes < 1) {
      return 'just now'
    } else if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`
    } else {
      const date = new Date(ts)
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
    }
  }

  return (
    <div className="flex justify-center py-3">
      <span className="text-[10px] text-muted-foreground/60 uppercase tracking-widest font-medium">
        {formatRelativeTime(timestamp)}
      </span>
    </div>
  )
}

// Format seconds as MM:SS or SS.Xs
function formatTimestamp(seconds: number): string {
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`
  }
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Pre-process content to convert timestamp patterns to markdown links
// Patterns: [t:2.5] for point, [t:2.5-4.0] for range
function processTimestamps(content: string): string {
  return content.replace(/\[t:([\d.]+)(?:-([\d.]+))?\]/g, (_, start, end) => {
    const startNum = parseFloat(start)
    if (end) {
      const endNum = parseFloat(end)
      return `[⏱ ${formatTimestamp(startNum)}-${formatTimestamp(endNum)}](timestamp:${startNum}-${endNum})`
    }
    return `[⏱ ${formatTimestamp(startNum)}](timestamp:${startNum})`
  })
}

// Timestamp link component that uses the video seek context
function TimestampLink({ href, children }: { href: string; children?: React.ReactNode }) {
  const videoSeek = useVideoSeekOptional()

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!videoSeek) return

    const match = href.match(/^timestamp:([\d.]+)(?:-([\d.]+))?$/)
    if (!match) return

    const start = parseFloat(match[1])
    const end = match[2] ? parseFloat(match[2]) : undefined

    if (end !== undefined) {
      videoSeek.playRange(start, end)
    } else {
      videoSeek.seekTo(start)
    }
  }, [href, videoSeek])

  return (
    <button
      type="button"
      onClick={handleClick}
      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-primary/10 text-primary hover:bg-primary/15 text-[13px] font-medium transition-colors"
    >
      {children}
    </button>
  )
}

// Shared audio instance so only one preview plays at a time
let globalPreviewAudio: HTMLAudioElement | null = null
let globalPreviewCleanup: (() => void) | null = null

function InlineAudioPreview({ href, children }: { href: string; children?: React.ReactNode }) {
  const [playing, setPlaying] = useState(false)

  useEffect(() => {
    return () => {
      if (globalPreviewAudio?.src === href) {
        globalPreviewAudio.pause()
        globalPreviewAudio = null
        globalPreviewCleanup = null
      }
    }
  }, [href])

  const toggle = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (playing) {
      globalPreviewAudio?.pause()
      globalPreviewAudio = null
      setPlaying(false)
      return
    }
    // Stop any other preview
    globalPreviewCleanup?.()
    const audio = new Audio(href)
    globalPreviewAudio = audio
    const stop = () => { setPlaying(false); globalPreviewAudio = null; globalPreviewCleanup = null }
    globalPreviewCleanup = stop
    audio.onended = stop
    audio.onerror = stop
    audio.play()
    setPlaying(true)
  }, [href, playing])

  return (
    <button
      type="button"
      onClick={toggle}
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[13px] font-medium transition-all',
        playing
          ? 'bg-blue-500 text-white'
          : 'bg-muted hover:bg-blue-500/10 text-muted-foreground hover:text-blue-500',
      )}
    >
      {playing ? <Pause size={10} /> : <Play size={10} className="ml-0.5" />}
      {children}
    </button>
  )
}

function isAudioUrl(href: string | undefined): boolean {
  if (!href) return false
  const lower = href.toLowerCase()
  if (/\.(mp3|wav|ogg|m4a|aac|webm)(\?|$)/.test(lower)) return true
  if (lower.includes(`${CDN_URL}/audio/`.toLowerCase())) return true
  return false
}

function getVoicePreviewUrl(children: React.ReactNode): string | null {
  const text = typeof children === 'string' ? children : Array.isArray(children)
    ? children.filter(c => typeof c === 'string').join('')
    : null
  if (!text) return null
  return getPresetVoicePreviewUrl(text.trim())
}

const markdownComponents = {
  p: ({ children }: { children?: React.ReactNode }) => <p className="mb-2 last:mb-0">{children}</p>,
  ul: ({ children }: { children?: React.ReactNode }) => <ul className="list-disc list-inside mb-2 last:mb-0">{children}</ul>,
  ol: ({ children }: { children?: React.ReactNode }) => <ol className="list-decimal list-inside mb-2 last:mb-0">{children}</ol>,
  li: ({ children }: { children?: React.ReactNode }) => <li>{children}</li>,
  strong: ({ children }: { children?: React.ReactNode }) => {
    const voiceUrl = getVoicePreviewUrl(children)
    if (voiceUrl) {
      return (
        <span className="inline-flex items-center gap-1">
          <strong className="font-semibold">{children}</strong>
          <InlineAudioPreview href={voiceUrl}><Volume2 size={10} /></InlineAudioPreview>
        </span>
      )
    }
    return <strong className="font-semibold">{children}</strong>
  },
  em: ({ children }: { children?: React.ReactNode }) => <em className="italic">{children}</em>,
  h1: ({ children }: { children?: React.ReactNode }) => <h1 className="text-[17px] font-semibold mb-2">{children}</h1>,
  h2: ({ children }: { children?: React.ReactNode }) => <h2 className="text-[15px] font-semibold mb-2">{children}</h2>,
  h3: ({ children }: { children?: React.ReactNode }) => <h3 className="text-[15px] font-medium mb-1">{children}</h3>,
  code: ({ children }: { children?: React.ReactNode }) => <code className="bg-muted px-1 py-0.5 rounded text-[13px]">{children}</code>,
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => {
    if (href?.startsWith('timestamp:')) {
      return <TimestampLink href={href}>{children}</TimestampLink>
    }
    if (isAudioUrl(href)) {
      return <InlineAudioPreview href={href!}>{children}</InlineAudioPreview>
    }
    return (
      <a
        href={href}
        className="text-primary hover:text-primary/80 underline underline-offset-2 font-medium break-all"
      >
        {children}
      </a>
    )
  },
}

const EMPTY_MAP = new Map<string, 'ready' | 'cancelled' | 'failed'>()

const LOADING_PHRASES = [
  "Rolling...",
  "Quiet on set...",
  "Lights, camera...",
  "Making movie magic...",
  "Cue the montage...",
  "Scene in progress...",
  "The show must go on...",
  "Picture's up...",
  "Stay tuned...",
  "In production...",
]

function LoadingPhrase() {
  const [index, setIndex] = useState(() => Math.floor(Math.random() * LOADING_PHRASES.length))
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false)
      setTimeout(() => {
        setIndex(i => (i + 1) % LOADING_PHRASES.length)
        setVisible(true)
      }, 400)
    }, 6000)

    return () => clearInterval(interval)
  }, [])

  return (
    <span
      className={cn(
        "text-[15px] text-muted-foreground transition-opacity duration-300",
        visible ? "opacity-100" : "opacity-0"
      )}
    >
      {LOADING_PHRASES[index]}
    </span>
  )
}

// Bouncing dots + cycling phrase shown while the agent is working. When the run
// start time is known, appends a live elapsed timer ("· 0:42"). Isolated so the
// 1s tick re-renders only this row, not the whole chat.
function WorkingIndicator({ startTime }: { startTime?: number }) {
  return (
    <div className="flex items-center gap-2.5 pl-1 py-1">
      <div className="flex items-center gap-0.5">
        <div className="w-1 h-1 bg-muted-foreground rounded-full animate-[bounce_1.4s_ease-in-out_infinite]" />
        <div className="w-1 h-1 bg-muted-foreground rounded-full animate-[bounce_1.4s_ease-in-out_0.2s_infinite]" />
        <div className="w-1 h-1 bg-muted-foreground rounded-full animate-[bounce_1.4s_ease-in-out_0.4s_infinite]" />
      </div>
      <LoadingPhrase />
      {startTime !== undefined && <WorkingElapsed startTime={startTime} />}
    </div>
  )
}

function WorkingElapsed({ startTime }: { startTime: number }) {
  const elapsed = useElapsedSeconds(startTime)
  return (
    <span className="text-[13px] tabular-nums text-muted-foreground/60">
      · {formatElapsed(elapsed)}
    </span>
  )
}

function NewAssetsBadge({ workflowId }: { workflowId: Id<'workflows'> }) {
  const res = useQuery(api.assets.getNewGeneratedAssetCount, { workflowId }) as
    | { count: number; capped: boolean; latestImageUrl?: string }
    | undefined
  const prevCountRef = useRef(0)
  const [peekVisible, setPeekVisible] = useState(false)
  const [peekUrl, setPeekUrl] = useState<string | null>(null)
  const [animateKey, setAnimateKey] = useState(0)
  const peekTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const peekCleanupRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    const count = res?.count ?? 0
    if (count > prevCountRef.current && count > 0) {
      setAnimateKey((k) => k + 1)

      if (res?.latestImageUrl) {
        clearTimeout(peekTimerRef.current)
        clearTimeout(peekCleanupRef.current)
        setPeekUrl(res.latestImageUrl)
        setPeekVisible(false)

        requestAnimationFrame(() => {
          setPeekVisible(true)
        })

        peekTimerRef.current = setTimeout(() => {
          setPeekVisible(false)
          peekCleanupRef.current = setTimeout(() => setPeekUrl(null), 350)
        }, 3500)
      }
    }
    prevCountRef.current = count
    return () => {
      clearTimeout(peekTimerRef.current)
      clearTimeout(peekCleanupRef.current)
    }
  }, [res?.count, res?.latestImageUrl])

  if (!res?.count) return null

  return (
    <>
      {animateKey > 0 && (
        <span
          key={`ping-${animateKey}`}
          className="absolute -top-1.5 -right-1.5 min-w-[20px] h-[20px] rounded-full bg-primary/40 z-9 pointer-events-none animate-[badge-ring_1.5s_ease-out_forwards]"
        />
      )}
      <span
        key={animateKey}
        className="absolute -top-1.5 -right-1.5 min-w-[20px] h-[20px] px-1 rounded-full bg-primary text-white text-[11px] font-bold flex items-center justify-center border-2 border-white shadow-[0_0_10px_oklch(68.5%_0.169_237.323/0.5)] z-10 animate-[badge-pop_0.5s_ease-out]"
      >
        {res.capped ? '99+' : res.count}
      </span>
      {peekUrl && (
        <div
          className={cn(
            'absolute -top-[60px] -right-0.5 z-20 transition-all duration-300 pointer-events-none',
            peekVisible
              ? 'opacity-100 translate-y-0 scale-100'
              : 'opacity-0 translate-y-2 scale-90'
          )}
        >
          <div className="w-11 h-11 rounded-lg overflow-hidden border-2 border-white shadow-lg ring-1 ring-black/10">
            <img src={peekUrl} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-white border-r border-b border-black/10 rotate-45" />
        </div>
      )}
    </>
  )
}

// Keyframe-related tools (feature now enabled for all users)
const KEYFRAME_TOOLS = ['set_clip_keyframe', 'set_clip_image', 'regenerate_keyframe']
export const HIDDEN_TOOL_CALLS = new Set(['show_upgrade_card', 'show_manage_subscription', 'acknowledge_notification', 'show_media_grid', 'show_clusters', 'show_folder_preview', 'suggest_community_chat', 'suggest_support_chat'])

export function MessageBubble({
  message,
  messageTerminalStatus = EMPTY_MAP,
  skipToolCalls = false,
  onRetry,
  retryDisabled = false,
  onImageClick,
  isAdmin = false,
  onSendSelection,
  onSendFiles,
  existingAnswer,
  workflowId,
  latestTreatmentToolId,
  typewriter,
}: {
  message: ChatMessage
  messageTerminalStatus?: Map<string, 'ready' | 'cancelled' | 'failed'>
  skipToolCalls?: boolean
  onRetry?: () => void
  retryDisabled?: boolean
  onImageClick?: (url: string) => void
  isAdmin?: boolean
  onSendSelection?: (message: string) => void
  onSendFiles?: (message: string, files: File[]) => void
  existingAnswer?: string
  workflowId?: Id<'workflows'> | null
  latestTreatmentToolId?: string | null
  // Replay mode: animate streaming text at a custom speed and signal when done
  typewriter?: { charsPerFrame: number; onComplete: () => void }
}) {
  const hasKeyframesFeature = true
  const isUser = message.role === 'user'
  const isSystem = message.role === 'system'
  const hasContentAfterTools = message.contentAfterTools && message.contentAfterTools.trim()

  if (isSystem) {
    return <SystemMessage message={message} messageTerminalStatus={messageTerminalStatus} />
  }

  // Check for upgrade card tool call
  const upgradeToolCall = message.toolCalls?.find(
    (tc) => tc.name === 'show_upgrade_card' &&
    tc.status === 'completed' &&
    tc.result?.success &&
    (tc.result?.data as { type?: string })?.type === 'upgrade_card'
  )
  const upgradeData = upgradeToolCall?.result?.data as {
    creditsNeeded: number
    creditsAvailable: number
    context?: string
  } | undefined

  // Check for manage subscription card tool call
  const manageSubToolCall = message.toolCalls?.find(
    (tc) => tc.name === 'show_manage_subscription' &&
    tc.status === 'completed' &&
    tc.result?.success &&
    (tc.result?.data as { type?: string })?.type === 'manage_subscription'
  )
  const manageSubData = manageSubToolCall?.result?.data as {
    portalUrl: string
    reason: string
  } | undefined

  // Check for community chat suggestion tool call
  const communityToolCall = message.toolCalls?.find(
    (tc) => tc.name === 'suggest_community_chat' &&
    tc.status === 'completed' &&
    tc.result?.success &&
    (tc.result?.data as { type?: string })?.type === 'community_chat_suggestion'
  )
  const communityData = communityToolCall?.result?.data as {
    reason?: string
  } | undefined

  // Check for support chat suggestion tool call
  const supportToolCall = message.toolCalls?.find(
    (tc) => tc.name === 'suggest_support_chat' &&
    tc.status === 'completed' &&
    tc.result?.success &&
    (tc.result?.data as { type?: string })?.type === 'support_chat_suggestion'
  )
  const supportData = supportToolCall?.result?.data as {
    reason?: string
  } | undefined

  // Filter out internal/meta tool calls from normal display (unless skipping all)
  const otherToolCalls = skipToolCalls ? [] : message.toolCalls?.filter((tc) => {
    if (HIDDEN_TOOL_CALLS.has(tc.name)) return false
    if (!hasKeyframesFeature && KEYFRAME_TOOLS.includes(tc.name)) return false
    return true
  })
  const hasOtherToolCalls = otherToolCalls && otherToolCalls.length > 0

  // Always render with markdown - simpler and avoids the "blink" when switching
  const renderContent = (content: string) => {
    const processed = processTimestamps(content)
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={markdownComponents}
        urlTransform={(url) => url}
      >
        {processed}
      </ReactMarkdown>
    )
  }

  const hasAttachments = message.attachments && message.attachments.length > 0
  const imageAttachments = message.attachments?.filter((att): att is ImageAttachment => att.type === 'image') || []
  const audioAttachments = message.attachments?.filter((att): att is AudioAttachment => att.type === 'audio') || []
  const documentAttachments = message.attachments?.filter((att): att is DocumentAttachment => att.type === 'document') || []
  const parsedErrorContent = !isUser ? parseAgentErrorContent(message.content) : { visibleContent: message.content, error: null }
  const contentToRender = parsedErrorContent.visibleContent?.trim() || null
  const agentError = parsedErrorContent.error

  return (
    <div className={`flex flex-col gap-1 ${isUser ? 'items-end' : 'items-start'}`}>
      {/* User attachments */}
      {hasAttachments && (
        <div className={`flex flex-wrap gap-2 max-w-[85%] ${isUser ? 'justify-end' : 'justify-start'}`}>
          {/* Images */}
          {imageAttachments.map((att, i) => (
            <img
              key={`img-${i}`}
              src={att.url}
              alt={att.name || 'Uploaded image'}
              className="max-w-[200px] max-h-[200px] rounded-lg object-cover cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => onImageClick?.(att.url)}
            />
          ))}
          {/* Audio */}
          {audioAttachments.map((att, i) => (
            <div
              key={`audio-${i}`}
              className="flex items-center gap-2 bg-muted rounded-lg px-3 py-2"
            >
              <div className="w-8 h-8 rounded-full bg-muted-foreground/20 flex items-center justify-center shrink-0">
                <span className="text-muted-foreground text-[13px]">🎵</span>
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-[13px] font-medium text-secondary-foreground truncate max-w-[150px]">
                  {att.name || 'Audio'}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  {att.duration.toFixed(1)}s
                </span>
              </div>
            </div>
          ))}
          {/* Documents */}
          {documentAttachments.map((att, i) => (
            <a
              key={`doc-${i}`}
              href={att.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-primary/10 rounded-lg px-3 py-2 cursor-pointer hover:bg-primary/20 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                <span className="text-primary text-[11px] font-bold">
                  {att.name?.split('.').pop()?.toUpperCase()?.slice(0, 4) || 'DOC'}
                </span>
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-[13px] font-medium text-secondary-foreground truncate max-w-[150px]">
                  {att.name || 'Document'}
                </span>
                <span className="text-[11px] text-primary">
                  {att.pageCount ? `${att.pageCount} pages` : att.mimeType.split('/').pop()}
                </span>
              </div>
            </a>
          ))}
        </div>
      )}

      {contentToRender && (
        <div className={`max-w-[85%] inline-block rounded-xl px-3.5 py-2 overflow-hidden ${
          isUser
            ? 'bg-foreground text-background'
            : 'bg-secondary text-secondary-foreground border border-border/50'
        }`}>
          <div className={`prose prose-sm max-w-none text-[15px] leading-normal ${isUser ? 'prose-invert' : ''}`}>
            {message.role === 'assistant' && message.isStreaming && contentToRender ? (
              <StreamingText
                content={contentToRender}
                markdownComponents={markdownComponents}
                charsPerFrame={typewriter?.charsPerFrame}
                onComplete={typewriter?.onComplete}
              />
            ) : (
              renderContent(contentToRender)
            )}
          </div>
        </div>
      )}

      {!isUser && agentError && (
        <div className="max-w-[85%] rounded-xl border border-destructive/20 bg-destructive/5 px-3 py-2 text-[15px] text-destructive">
          <div className="font-medium">Something went wrong</div>
          <div className="mt-0.5 text-destructive">
            {friendlyErrorMessage(agentError) || (isAdmin ? agentError : 'An unexpected error occurred. Please try again.')}
          </div>
          {isAdmin && friendlyErrorMessage(agentError) && (
            <details className="mt-1">
              <summary className="cursor-pointer text-[13px] text-destructive/70 hover:text-destructive">Details</summary>
              <div className="mt-1 text-[13px] text-destructive/70 break-all">{agentError}</div>
            </details>
          )}
          {onRetry && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={retryDisabled}
              onClick={onRetry}
              className="mt-2 h-7 border-destructive/30 bg-background text-destructive hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
            >
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
              Retry
            </Button>
          )}
        </div>
      )}

      {hasOtherToolCalls && (
        <div className="max-w-[85%]">
          <ToolCallsSection tools={otherToolCalls!} />
        </div>
      )}

      {/* Render upgrade card if show_upgrade_card tool was called */}
      {upgradeData && (
        <div className="max-w-[85%] w-full">
          <UpgradeCard />
        </div>
      )}

      {/* Render manage subscription card if show_manage_subscription tool was called */}
      {manageSubData && (
        <div className="max-w-[85%] w-full">
          <ManageSubscriptionCard portalUrl={manageSubData.portalUrl} reason={manageSubData.reason} />
        </div>
      )}

      {/* Render community chat suggestion if suggest_community_chat tool was called */}
      {communityData && (
        <div className="max-w-[85%] w-full">
          <CommunityChatCard />
        </div>
      )}

      {/* Render support chat suggestion if suggest_support_chat tool was called */}
      {supportData && (
        <div className="max-w-[85%] w-full">
          <SupportChatCard />
        </div>
      )}

      {/* Render visual tool widgets (media grid, clusters, folder preview, treatment board) */}
      {message.toolCalls?.map((tc) => {
        if (tc.status !== 'completed' || !tc.result?.success) return null
        const data = tc.result?.data as { type?: string } | undefined
        if (!data?.type || !['question_options', 'question_sequence', 'media_grid', 'media_clusters', 'folder_preview', 'treatment_board', 'asset_request'].includes(data.type)) return null
        const isTreatment = data.type === 'treatment_board'
        const wide = widgetWantsWideLane(data as Record<string, unknown>)
        return (
          <div key={tc.id} className={wide ? 'w-full' : 'max-w-[85%] w-full'}>
            <VisualToolWidget
              data={data as Record<string, unknown>}
              onSendSelection={onSendSelection}
              onSendFiles={onSendFiles}
              existingAnswer={existingAnswer}
              workflowId={workflowId}
              superseded={isTreatment && latestTreatmentToolId != null && tc.id !== latestTreatmentToolId}
              widgetKey={tc.id}
            />
          </div>
        )
      })}

      {hasContentAfterTools && (
        <div className="max-w-[85%] inline-block rounded-xl px-3.5 py-2 bg-secondary text-secondary-foreground border border-border/50">
          <div className="prose prose-sm max-w-none text-[15px] leading-normal">
            {renderContent(message.contentAfterTools!)}
          </div>
        </div>
      )}
    </div>
  )
}

function FreezeSnapshotModal({
  workflowId,
  messageIndex,
  onClose,
}: {
  workflowId: string
  messageIndex: number
  onClose: () => void
}) {
  const createSnapshot = useMutation(api.evals.createSnapshot)
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<string | null>(null)

  const submit = async () => {
    setError(null)
    setSubmitting(true)
    try {
      const id = await createSnapshot({
        sourceWorkflowId: workflowId as any,
        messageIndex,
        name: `snap@${messageIndex}`,
        description: note.trim() || undefined,
        criteria: [],
      })
      setResult(String(id))
    } catch (err: any) {
      setError(err?.message ?? 'Failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-popover rounded-xl w-full max-w-xl shadow-xl border border-border" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <div className="flex items-center gap-2 text-[15px] font-medium">
            <Snowflake className="h-4 w-4 text-blue-500" />
            Freeze at message {messageIndex}
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>

        {result ? (
          <div className="p-5 space-y-3">
            <div className="text-[15px]">Snapshot created{note.trim() ? ' and issue filed.' : '.'}</div>
            <div className="text-[13px] font-mono bg-muted border border-border rounded px-2 py-1.5">{result}</div>
            <div className="text-[13px] text-muted-foreground">
              Replay: <code className="font-mono">bun scripts/admin.ts replay {result}</code>
            </div>
            <button onClick={onClose} className="text-[13px] px-3 py-1.5 rounded border border-border hover:bg-muted">Close</button>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div>
              <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wide">What's going wrong here?</label>
              <textarea
                autoFocus
                value={note}
                onChange={e => setNote(e.target.value)}
                rows={4}
                placeholder="Describe what the agent did wrong at this point..."
                className="mt-1 w-full text-[15px] px-3 py-2 border border-border rounded bg-background focus:outline-none focus:border-ring resize-none"
              />
            </div>

            {error && <div className="text-[13px] text-destructive">{error}</div>}

            <div className="flex gap-2 pt-1">
              <button onClick={submit} disabled={submitting} className="text-[13px] px-3 py-1.5 rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                {submitting ? 'Creating…' : 'Create snapshot'}
              </button>
              <button onClick={onClose} className="text-[13px] px-3 py-1.5 rounded border border-border hover:bg-muted">Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export function WorkflowChat({
  workflowId,
  messages,
  isStreaming,
  error,
  onSendMessage,
  onCancel,
  isUploading = false,
  uploadStatus,
  clips,
  hideComposer = false,
  narrowContent = false,
  scrollButtonClassName,
}: WorkflowChatProps) {
  const { user } = useAuth()
  const isAdmin = user?.isAdmin === true
  const agentRunStartedAt = useQuery(api.workflows.get, workflowId ? { id: workflowId } : 'skip')?.agentRunStartedAt
  const {
    input, setInput,
    pendingImages, setPendingImages,
    pendingAudio, setPendingAudio,
    pendingDocuments, setPendingDocuments,
    pendingLibraryAttachments, setPendingLibraryAttachments,
    clearDraft,
  } = useChatDraft(workflowId)
  const [isAssetLibraryOpen, setIsAssetLibraryOpen] = useState(false)
  const [assetUploadProgress, setAssetUploadProgress] = useState<{ current: number; total: number } | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isScrolledUp, setIsScrolledUp] = useState(false)
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)
  const [freezeAt, setFreezeAt] = useState<number | null>(null)
  const [newMessageCount, setNewMessageCount] = useState(0)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const lastMessageCountRef = useRef(0)
  const hasScrolledInitiallyRef = useRef(false)
  const SCROLL_THRESHOLD = 300
  const hasKeyframesFeature = true

  const markAssetsSeenForWorkflow = useMutation(api.assets.markAssetsSeenForWorkflow)
  const createClipFromAsset = useMutation(api.workflows.createClipFromAsset)

  function AssetLibraryUnavailableSheet({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
    return (
      <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/30 backdrop-blur-[2px]" />
          <DialogPrimitive.Content
            className={cn(
              'fixed z-50 left-0 right-0 bottom-0',
              'max-h-[82vh] w-full',
              'rounded-t-2xl border-t border-border bg-background shadow-[0_-20px_60px_-20px_rgba(0,0,0,0.35)]',
              'focus:outline-none',
              'p-4'
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <DialogPrimitive.Title className="text-[15px] font-semibold text-foreground">
                  Asset Library
                </DialogPrimitive.Title>
                <p className="mt-1 text-[13px] text-muted-foreground">
                  Backend for this panel is not deployed yet, so assets can’t be loaded.
                </p>
              </div>
              <DialogPrimitive.Close
                className="shrink-0 w-9 h-9 rounded-lg bg-muted text-secondary-foreground hover:bg-muted-foreground/20 hover:text-foreground transition-colors flex items-center justify-center"
                aria-label="Close"
              >
                <X size={16} />
              </DialogPrimitive.Close>
            </div>

            <div className="mt-4">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="h-9 px-3 rounded-lg text-[13px] border border-border text-secondary-foreground hover:border-border hover:bg-accent transition-colors"
              >
                Close
              </button>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    )
  }

  // Create clip lookup context
  const toolDisplayContext = useMemo<ToolDisplayContext>(() => {
    if (!clips?.length) return {}
    const clipMap = new Map(clips.map((s, i) => [s._id, i + 1]))
    return {
      getSceneNumber: (sceneId: string) => clipMap.get(sceneId),
    }
  }, [clips])

  // Only the newest treatment board renders expanded; older ones collapse to a
  // superseded chip so re-shows don't litter the transcript.
  const latestTreatmentToolId = useMemo(() => computeLatestTreatmentToolId(messages), [messages])

  // Aggregate tool calls between user/system messages
  const aggregatedToolCalls = useMemo(() => computeAggregatedToolCalls(messages), [messages])

  // Track terminal status for each "generation begun" message by its ID
  // Each begun message is matched to its terminal message (the next ready/cancelled/failed for that scene)
  const messageTerminalStatus = useMemo(() => computeMessageTerminalStatus(messages), [messages])

  const checkIfScrolledUp = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) return false
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight
    return distanceFromBottom > SCROLL_THRESHOLD
  }, [SCROLL_THRESHOLD])

  const scrollToBottom = useCallback(() => {
    const container = scrollContainerRef.current
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' })
    }
    setNewMessageCount(0)
    setIsScrolledUp(false)
  }, [])

  const handleScroll = useCallback(() => {
    const scrolledUp = checkIfScrolledUp()
    setIsScrolledUp(scrolledUp)
    if (!scrolledUp) {
      setNewMessageCount(0)
    }
  }, [checkIfScrolledUp])

  useEffect(() => {
    const newCount = messages.length
    const prevCount = lastMessageCountRef.current

    if (newCount > prevCount) {
      if (isScrolledUp) {
        setNewMessageCount(prev => prev + (newCount - prevCount))
      } else {
        const container = scrollContainerRef.current
        if (container) {
          const behavior: ScrollBehavior = hasScrolledInitiallyRef.current ? 'smooth' : 'auto'
          hasScrolledInitiallyRef.current = true
          container.scrollTo({ top: container.scrollHeight, behavior })
        }
      }
    }

    lastMessageCountRef.current = newCount
  }, [messages.length, isScrolledUp])

  // Auto-scroll during streaming when user is near bottom
  const streamingContentRef = useRef('')
  useEffect(() => {
    if (!isStreaming) {
      streamingContentRef.current = ''
      return
    }
    const streamingMsg = messages.find(m => m.role === 'assistant' && m.isStreaming)
    const content = streamingMsg?.content || ''
    const toolCount = (streamingMsg?.toolCalls?.length ?? 0)
    const key = `${content.length}:${toolCount}`
    if (key === streamingContentRef.current) return
    streamingContentRef.current = key
    if (!checkIfScrolledUp()) {
      const container = scrollContainerRef.current
      if (container) {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' })
      }
    }
  }, [isStreaming, messages, checkIfScrolledUp])

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      pendingImages.forEach(img => URL.revokeObjectURL(img.preview))
    }
  }, [pendingImages])

  // Supported audio MIME types
  const AUDIO_TYPES = ['audio/mpeg', 'audio/mp4', 'audio/wav', 'audio/x-m4a', 'audio/aac', 'audio/x-wav']

  // Supported document MIME types
  const DOCUMENT_TYPES = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-powerpoint',
    'application/msword',
    'text/plain',
    'text/markdown',
    'text/csv',
  ]

  // Get audio duration using Web Audio API
  const getAudioDuration = (file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
      const audio = new Audio()
      audio.preload = 'metadata'
      audio.onloadedmetadata = () => {
        URL.revokeObjectURL(audio.src)
        resolve(audio.duration)
      }
      audio.onerror = () => {
        URL.revokeObjectURL(audio.src)
        reject(new Error('Failed to load audio'))
      }
      audio.src = URL.createObjectURL(file)
    })
  }

  const handleFiles = async (files: FileList | File[]) => {
    const fileArray = Array.from(files)

    // Handle images
    const imageFiles = fileArray.filter(f => f.type.startsWith('image/'))
    const newImages = imageFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
    }))
    if (newImages.length > 0) {
      setPendingImages(prev => [...prev, ...newImages])
    }

    // Handle audio files
    const audioFiles = fileArray.filter(f => AUDIO_TYPES.includes(f.type))
    for (const file of audioFiles) {
      // Validate size (max 50MB)
      if (file.size > 50 * 1024 * 1024) {
        console.error(`Audio file ${file.name} is too large (max 50MB)`)
        continue
      }
      try {
        const duration = await getAudioDuration(file)
        // Validate duration (0.5 - 300 seconds)
        if (duration < 0.5 || duration > 300) {
          console.error(`Audio file ${file.name} duration out of range (0.5s - 5min)`)
          continue
        }
        setPendingAudio(prev => [...prev, { file, duration }])
      } catch {
        console.error(`Could not read audio file ${file.name}`)
      }
    }

    // Handle document files
    const docFiles = fileArray.filter(f => DOCUMENT_TYPES.includes(f.type))
    for (const file of docFiles) {
      if (file.size > 100 * 1024 * 1024) {
        console.error(`Document ${file.name} is too large (max 100MB)`)
        continue
      }
      setPendingDocuments(prev => [...prev, { file }])
    }
  }

  const removeImage = (index: number) => {
    setPendingImages(prev => {
      const removed = prev[index]
      if (removed) {
        URL.revokeObjectURL(removed.preview)
      }
      return prev.filter((_, i) => i !== index)
    })
  }

  const removeAudio = (index: number) => {
    setPendingAudio(prev => prev.filter((_, i) => i !== index))
  }

  const removeDocument = (index: number) => {
    setPendingDocuments(prev => prev.filter((_, i) => i !== index))
  }

  const removeLibraryAttachment = (index: number) => {
    setPendingLibraryAttachments(prev => prev.filter((_, i) => i !== index))
  }

  const dragCounter = useRef(0)

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current++
    if (e.dataTransfer.types.includes('Files')) setIsDragging(true)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current--
    if (dragCounter.current <= 0) {
      dragCounter.current = 0
      setIsDragging(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current = 0
    setIsDragging(false)
    if (e.dataTransfer.files.length) {
      handleFiles(e.dataTransfer.files)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // Don't block on isStreaming - allow queuing messages
    if ((!input.trim() && pendingImages.length === 0 && pendingAudio.length === 0 && pendingDocuments.length === 0 && pendingLibraryAttachments.length === 0) || isUploading) return

    const imageFiles = pendingImages.map(img => img.file)
    const audioFiles = pendingAudio.map(a => a.file)
    const docFiles = pendingDocuments.map(d => d.file)
    onSendMessage(input, {
      images: imageFiles.length > 0 ? imageFiles : undefined,
      audioFiles: audioFiles.length > 0 ? audioFiles : undefined,
      documents: docFiles.length > 0 ? docFiles : undefined,
      attachments: pendingLibraryAttachments.length > 0 ? pendingLibraryAttachments : undefined,
    })
    clearDraft()
    // Reset textarea height after submit
    if (inputRef.current) {
      inputRef.current.style.height = '44px'
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  // Input is only disabled during upload, not during streaming (allows typing while waiting)
  const isInputDisabled = isUploading
  // Submit is enabled during streaming (message will be queued) but disabled during uploading
  const canSubmit = (input.trim() || pendingImages.length > 0 || pendingAudio.length > 0 || pendingDocuments.length > 0 || pendingLibraryAttachments.length > 0) && !isUploading

  return (
    <ToolDisplayContextProvider.Provider value={toolDisplayContext}>
    <UpgradeModalProvider>
    <div
      className={cn(
        "flex flex-col h-full bg-background relative",
        isDragging && !hideComposer && "ring-2 ring-inset ring-primary"
      )}
      onDragEnter={hideComposer ? undefined : handleDragEnter}
      onDragOver={hideComposer ? undefined : handleDragOver}
      onDragLeave={hideComposer ? undefined : handleDragLeave}
      onDrop={hideComposer ? undefined : handleDrop}
    >
      {/* Drag overlay */}
      {isDragging && !hideComposer && (
        <div className="absolute inset-0 bg-primary/5 flex items-center justify-center z-20 pointer-events-none">
          <div className="text-center">
            <Plus className="h-10 w-10 text-primary mx-auto mb-2" />
            <p className="text-primary font-medium">Drop images, audio, or documents here</p>
          </div>
        </div>
      )}

      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className={cn(
          "flex-1 min-h-0 overflow-y-auto pt-3 space-y-3 relative",
          narrowContent
            ? "px-8 pb-48 [&>*]:max-w-[720px] [&>*]:mx-auto [scrollbar-gutter:stable_both-edges]"
            : "px-4 pb-32",
        )}
      >
        {/* Only show empty state if NOT streaming - prevents flash when sending first message */}
        {messages.length === 0 && !isStreaming && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
            <MessageCircle size={32} className="text-muted-foreground/50 mb-3" />
            <h3 className="text-[15px] font-medium text-secondary-foreground mb-1">
              Start a conversation
            </h3>
            <p className="text-muted-foreground text-[15px]">
              Describe the video you want to create
            </p>
          </div>
        )}

        {messages.map((message, index) => {
          // Get aggregated tools for this position (if any)
          const aggregatedTools = aggregatedToolCalls.groups.get(index)
          // Skip individual tool rendering if:
          // 1. This position has aggregated tools (we'll render those separately), OR
          // 2. This message's tools are part of an aggregate at a different position
          const skipToolCalls = !!aggregatedTools || message.toolCalls?.some(tc => aggregatedToolCalls.seenToolIds.has(tc.id))

          // Filter aggregated tools (reused for visibility check and rendering)
          const filteredAggregatedTools = aggregatedTools?.filter(tc => {
            if (HIDDEN_TOOL_CALLS.has(tc.name)) return false
            if (!hasKeyframesFeature && KEYFRAME_TOOLS.includes(tc.name)) return false
            return true
          })
          const hasVisibleAggregatedTools = filteredAggregatedTools && filteredAggregatedTools.length > 0

          // Skip selection messages from question pickers (prefixed with zero-width space)
          if (message.role === 'user' && message.content?.startsWith('\u200B')) {
            return null
          }

          // Skip assistant messages that would render nothing visible
          // This happens when tools are aggregated elsewhere and there's no text content
          if (message.role === 'assistant') {
            const hasContent = message.content?.trim() || message.contentAfterTools?.trim()
            // Check if this message has an upgrade card or visual widgets (renders even when skipToolCalls)
            const hasSpecialWidget = message.toolCalls?.some(
              tc => tc.status === 'completed' &&
                   tc.result?.success &&
                   ['upgrade_card', 'manage_subscription', 'question_options', 'question_sequence', 'media_grid', 'media_clusters', 'folder_preview', 'treatment_board', 'asset_request', 'community_chat_suggestion', 'support_ticket_submitted'].includes(
                     (tc.result?.data as { type?: string })?.type || ''
                   )
            )

            if (!hasContent && !hasVisibleAggregatedTools && !hasSpecialWidget) {
              return null
            }
          }

          // Check if we need a timestamp separator before this message
          const prevMessage = index > 0 ? messages[index - 1] : null
          const showTimestampSeparator = message.timestamp && (
            !prevMessage?.timestamp ||
            (message.timestamp - prevMessage.timestamp) > TIMESTAMP_GAP_MINUTES * 60 * 1000
          )

          // Find existing Q&A answer (next \u200B-prefixed user message) for reload persistence
          const existingAnswer = findExistingAnswer(messages, index)

          // Visual widgets (casting strips, treatment board, media grids) break
          // out of the readable text column into a wider centered lane when the
          // pane has the room \u2014 `!` overrides the container's [&>*]:max-w-[720px].
          const hasWideWidget = narrowContent && message.toolCalls?.some(
            tc => tc.status === 'completed' && tc.result?.success &&
                  widgetWantsWideLane(tc.result?.data as Record<string, unknown>),
          )

          return (
            <div key={message.id} data-message-role={message.role} data-streaming={message.isStreaming ? "true" : undefined} className={cn('group/msg relative', hasWideWidget && '!max-w-[960px]')}>
              {showTimestampSeparator && (
                <TimestampSeparator timestamp={message.timestamp!} />
              )}
              <MessageBubble
                message={message}
                messageTerminalStatus={messageTerminalStatus}
                skipToolCalls={skipToolCalls}
                retryDisabled={isStreaming || isUploading}
                onImageClick={setLightboxUrl}
                isAdmin={isAdmin}
                onSendSelection={(msg) => onSendMessage(msg)}
                onSendFiles={(msg, files) => {
                  const images = files.filter(f => f.type.startsWith('image/') || f.type.startsWith('video/'))
                  const audioFiles = files.filter(f => f.type.startsWith('audio/'))
                  const documents = files.filter(f => !f.type.startsWith('image/') && !f.type.startsWith('video/') && !f.type.startsWith('audio/'))
                  onSendMessage(msg, {
                    images: images.length ? images : undefined,
                    audioFiles: audioFiles.length ? audioFiles : undefined,
                    documents: documents.length ? documents : undefined,
                  })
                }}
                existingAnswer={existingAnswer}
                workflowId={workflowId}
                latestTreatmentToolId={latestTreatmentToolId}
                onRetry={(() => {
                  if (message.role !== 'assistant') return undefined
                  const { error: agentError } = parseAgentErrorContent(message.content)
                  if (!agentError || isStreaming || isUploading) return undefined

                  for (let i = index - 1; i >= 0; i--) {
                    const prev = messages[i]
                    if (prev.role !== 'user') continue
                    if (!prev.content.trim()) return undefined
                    return () => onSendMessage(prev.content)
                  }

                  return undefined
                })()}
              />
              {hasVisibleAggregatedTools && (
                <div className="max-w-[85%] mt-1">
                  <ToolCallsSection tools={filteredAggregatedTools} />
                </div>
              )}
              {isAdmin && workflowId && message.role !== 'system' && (
                <button
                  onClick={() => setFreezeAt(index + 1)}
                  title={`Freeze at message ${index + 1}`}
                  className="absolute right-1 top-1 opacity-0 group-hover/msg:opacity-100 transition-opacity p-1 rounded hover:bg-muted text-muted-foreground hover:text-blue-500"
                >
                  <Snowflake className="h-3 w-3" />
                </button>
              )}
            </div>
          )
        })}

        {/* Show loading indicator when agent is working but not actively streaming text */}
        {isStreaming && (() => {
          const streamingMsg = messages.find(m => m.role === 'assistant' && m.isStreaming)
          const hasVisibleText = streamingMsg?.content && streamingMsg.content.length > 0
          if (hasVisibleText) return null
          return <WorkingIndicator startTime={agentRunStartedAt} />
        })()}

        {error && (
          <div className="bg-destructive/10 text-destructive px-3 py-2 rounded-lg text-[15px]">
            {friendlyErrorMessage(error) || (isAdmin ? error : 'Something went wrong. Please try again.')}
          </div>
        )}

        {(uploadStatus || assetUploadProgress) && (
          <div className="mx-4 my-2 flex items-center gap-3 px-4 py-3 rounded-xl bg-primary/5 border border-primary/10">
            <Loader2 size={16} className="animate-spin text-primary shrink-0" />
            <span className="text-[15px] text-foreground">
              {uploadStatus || `Uploading ${assetUploadProgress!.current}/${assetUploadProgress!.total} files...`}
            </span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Scroll to bottom button */}
      {isScrolledUp && (
        <button
          type="button"
          onClick={scrollToBottom}
          className={cn(
            "absolute z-10 rounded-full bg-background border border-border/60 shadow-sm flex items-center justify-center hover:bg-accent transition-colors backdrop-blur-sm",
            scrollButtonClassName ?? (narrowContent
              ? "bottom-[152px] left-1/2 -translate-x-1/2 w-9 h-9"
              : "bottom-24 right-4 w-7 h-7"),
          )}
        >
          <ArrowDown size={narrowContent ? 16 : 14} className="text-muted-foreground" />
          {newMessageCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] rounded-full bg-primary text-white text-[11px] font-medium flex items-center justify-center px-1">
              {newMessageCount > 99 ? '99+' : newMessageCount}
            </span>
          )}
        </button>
      )}

      {!hideComposer && (
      <form onSubmit={handleSubmit} className="relative px-3 pb-3 pt-2">
        {/* Suggested action pills - absolutely positioned above form */}
        {(() => {
          if (isStreaming) return null
          const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant')
          if (!lastAssistant?.suggestedActions?.length) return null
          return (
            <div className="absolute bottom-full left-0 right-0 px-3 pb-2 pointer-events-none">
              <div className="flex flex-wrap justify-center gap-1.5">
                {lastAssistant.suggestedActions.map((action, i) => (
                  <button
                    key={i}
                    type="button"
                    data-suggested-action={action}
                    onClick={() => onSendMessage(action)}
                    className="pointer-events-auto text-[13px] px-3 py-1.5 rounded-lg border border-border/60 bg-background text-muted-foreground hover:border-foreground/20 hover:text-secondary-foreground transition-colors cursor-pointer"
                  >
                    {action}
                  </button>
                ))}
              </div>
            </div>
          )
        })()}
        {/* Pending media preview */}
        {(pendingImages.length > 0 || pendingAudio.length > 0 || pendingDocuments.length > 0 || pendingLibraryAttachments.length > 0) && (
          <div className="flex gap-2 pt-2 pb-3 mb-3 border-b border-border/50 overflow-x-auto">
            {/* Images */}
            {pendingImages.map((img, i) => (
              <div key={`img-${i}`} className="relative shrink-0">
                <img
                  src={img.preview}
                  alt={`Pending upload ${i + 1}`}
                  className="h-16 w-16 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => removeImage(i)}
                  className="absolute -top-1.5 -right-1.5 bg-foreground/80 text-background rounded-full w-5 h-5 flex items-center justify-center hover:bg-foreground transition-colors"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            {/* Audio files */}
            {pendingAudio.map((audio, i) => (
              <div key={`audio-${i}`} className="relative shrink-0 h-16 bg-muted rounded-lg px-3 flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-muted-foreground/20 flex items-center justify-center">
                  <span className="text-muted-foreground text-[13px]">🎵</span>
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-[13px] font-medium text-secondary-foreground truncate max-w-[100px]">
                    {audio.file.name}
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    {audio.duration.toFixed(1)}s
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => removeAudio(i)}
                  className="absolute -top-1.5 -right-1.5 bg-foreground/80 text-background rounded-full w-5 h-5 flex items-center justify-center hover:bg-foreground transition-colors"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            {/* Documents */}
            {pendingDocuments.map((doc, i) => (
              <div key={`doc-${i}`} className="relative shrink-0 h-16 bg-primary/10 rounded-lg px-3 flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center">
                  <span className="text-primary text-[13px] font-bold">
                    {doc.file.name.split('.').pop()?.toUpperCase()?.slice(0, 4) || 'DOC'}
                  </span>
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-[13px] font-medium text-secondary-foreground truncate max-w-[100px]">
                    {doc.file.name}
                  </span>
                  <span className="text-[11px] text-primary">
                    {(doc.file.size / 1024 / 1024).toFixed(1)}MB
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => removeDocument(i)}
                  className="absolute -top-1.5 -right-1.5 bg-foreground/80 text-background rounded-full w-5 h-5 flex items-center justify-center hover:bg-foreground transition-colors"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            {/* Library attachments */}
            {pendingLibraryAttachments.map((att, i) => {
              if (att.type === 'image') {
                return (
                  <div key={`lib-img-${i}`} className="relative shrink-0">
                    <img
                      src={att.url}
                      alt={`Library asset ${i + 1}`}
                      className="h-16 w-16 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => removeLibraryAttachment(i)}
                      className="absolute -top-1.5 -right-1.5 bg-foreground/80 text-background rounded-full w-5 h-5 flex items-center justify-center hover:bg-foreground transition-colors"
                    >
                      <X size={12} />
                    </button>
                  </div>
                )
              }

              return (
                <div key={`lib-audio-${i}`} className="relative shrink-0 h-16 bg-muted rounded-lg px-3 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-muted-foreground/20 flex items-center justify-center">
                    <span className="text-muted-foreground text-[13px]">🎵</span>
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="text-[13px] font-medium text-secondary-foreground truncate max-w-[100px]">
                      {att.name || 'Audio'}
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      {att.type === 'audio' ? `${att.duration.toFixed(1)}s` : att.type}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeLibraryAttachment(i)}
                    className="absolute -top-1.5 -right-1.5 bg-foreground/80 text-background rounded-full w-5 h-5 flex items-center justify-center hover:bg-foreground transition-colors"
                  >
                    <X size={12} />
                  </button>
                </div>
              )
            })}
          </div>
        )}

        <div className="flex gap-1.5 items-end rounded-xl border border-border bg-background p-1.5 focus-within:ring-2 focus-within:ring-ring focus-within:border-transparent transition-all">
          {/* Attachment button */}
          <button
            type="button"
            onClick={() => setIsAssetLibraryOpen(true)}
            disabled={isInputDisabled || !workflowId}
            aria-label="Open asset library"
            className="relative shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-muted-foreground hover:bg-muted hover:text-secondary-foreground"
          >
            <Plus size={16} />
            {workflowId ? (
              <ErrorBoundary fallback={null}>
                <NewAssetsBadge workflowId={workflowId} />
              </ErrorBoundary>
            ) : null}
          </button>

          <div className="flex-1 relative">
            {/* Resize handle at top - drag upward to expand */}
            <div
              className="absolute -top-2 left-0 right-0 h-3 cursor-ns-resize group flex items-center justify-center z-10"
              onMouseDown={(e) => {
                e.preventDefault()
                const textarea = inputRef.current
                if (!textarea) return
                const startY = e.clientY
                const startHeight = textarea.offsetHeight
                const onMouseMove = (moveEvent: MouseEvent) => {
                  const delta = startY - moveEvent.clientY
                  const newHeight = Math.min(Math.max(startHeight + delta, 44), 500)
                  textarea.style.height = newHeight + 'px'
                }
                const onMouseUp = () => {
                  document.removeEventListener('mousemove', onMouseMove)
                  document.removeEventListener('mouseup', onMouseUp)
                }
                document.addEventListener('mousemove', onMouseMove)
                document.addEventListener('mouseup', onMouseUp)
              }}
            >
              <div className="w-6 h-px bg-border/60 rounded-full group-hover:bg-muted-foreground/50 transition-colors" />
            </div>
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => {
                setInput(e.target.value)
                e.target.style.height = 'auto'
                e.target.style.height = Math.min(e.target.scrollHeight, 500) + 'px'
              }}
              onKeyDown={handleKeyDown}
              onPaste={(e) => {
                const items = e.clipboardData?.items
                if (!items) return
                const imageFiles: File[] = []
                for (const item of items) {
                  if (item.type.startsWith('image/')) {
                    const file = item.getAsFile()
                    if (file) imageFiles.push(file)
                  }
                }
                if (imageFiles.length > 0) {
                  e.preventDefault()
                  handleFiles(imageFiles)
                }
              }}
              placeholder={pendingDocuments.length > 0 ? "Add a message about your document(s)..." : (pendingImages.length > 0 || pendingLibraryAttachments.some((a) => a.type === 'image')) ? "Add a message about your image(s)..." : "Describe your video..."}
              disabled={isInputDisabled}
              rows={1}
              className="block w-full resize-none rounded-lg border-0 px-2 py-1.5 text-[15px] focus:outline-none focus:ring-0 disabled:bg-secondary disabled:text-muted-foreground bg-transparent"
              style={{ minHeight: '36px', maxHeight: '500px' }}
            />
          </div>
          {isStreaming ? (
            <button
              type="button"
              onClick={onCancel}
              className="shrink-0 w-9 h-9 rounded-lg bg-destructive/8 text-destructive flex items-center justify-center hover:bg-destructive/12 transition-all"
            >
              <Square size={12} className="rounded-sm" fill="currentColor" />
            </button>
          ) : isUploading ? (
            <button
              type="button"
              disabled
              className="shrink-0 w-9 h-9 rounded-lg bg-muted text-muted-foreground flex items-center justify-center"
            >
              <Loader2 size={14} className="animate-spin" />
            </button>
          ) : (
            <button
              type="submit"
              disabled={!canSubmit}
              className="shrink-0 w-9 h-9 rounded-lg bg-foreground text-background flex items-center justify-center hover:bg-foreground/90 transition-colors disabled:bg-muted disabled:text-muted-foreground"
            >
              <ArrowUp size={16} />
            </button>
          )}
        </div>
      </form>
      )}

      {workflowId && (
        <ErrorBoundary
          key={`asset-lib:${workflowId}`}
          fallback={
            <AssetLibraryUnavailableSheet
              open={isAssetLibraryOpen}
              onOpenChange={setIsAssetLibraryOpen}
            />
          }
        >
          <AssetLibrarySheet
            workflowId={workflowId}
            open={isAssetLibraryOpen}
            onOpenChange={(nextOpen) => {
              if (!nextOpen && isAssetLibraryOpen) {
                markAssetsSeenForWorkflow({ workflowId }).catch(() => {})
              }
              setIsAssetLibraryOpen(nextOpen)
            }}
            onAddAttachment={(att) => {
              setPendingLibraryAttachments((prev) => [...prev, att])
            }}
            onAddToTimeline={async (assetId) => {
              try {
                await createClipFromAsset({ workflowId, assetId })
                toast({ title: 'Added to timeline' })
              } catch (e: any) {
                toast({ title: 'Failed to add to timeline', description: e.message, variant: 'destructive' })
              }
            }}
            onUploadProgress={setAssetUploadProgress}
          />
        </ErrorBoundary>
      )}
      <Lightbox url={lightboxUrl} type="image" onClose={() => setLightboxUrl(null)} />
      {freezeAt !== null && workflowId && (
        <FreezeSnapshotModal
          workflowId={workflowId}
          messageIndex={freezeAt}
          onClose={() => setFreezeAt(null)}
        />
      )}
    </div>
    </UpgradeModalProvider>
    </ToolDisplayContextProvider.Provider>
  )
}
